<template>
    <div
        class="md-form team-slider"
        :style="{ backgroundColor: bgColor || '' }"
    >
        <module-box
            :fullColumn="fullColumn"
            :topSpacing="topSpacing"
            :btmSpacing="btmSpacing"
            :mobileNoMargin="mobileNoMargin"
            :mobileTopSpacing="mobileTopSpacing"
            :mobileBtmSpacing="mobileBtmSpacing"
            class="team-slider-content"
        >
            <section class="md-team-member" v-if="teamMemberAgents.length > 0">
                <div
                    class="title"
                    v-html="formatFontSize(title || ' ', 30)"
                ></div>
                <div class="slides swiper-container">
                    <Swiper
                        class="member-slider" 
                        v-bind="options" 
                        :items="teamMemberAgents" 
                    >
                        <Slide v-for="(member, index) in teamMemberAgents" :key="index">
                            <a
                                :href="$ft.url(member.externalUrl)"
                                rel="noopener noreferrer"
                            >
                                <AgentHeadShot class="agent-image" :headshots="member.headShots" :initImage="member.image || '//static.chimeroi.com/site/images/about/default-avatar.png'" :alt="member.fullName" keyClass="md-form-team-slider" />
                            </a>
                            <div class="content">
                                <a
                                    :href="$ft.url(member.externalUrl)"
                                    class="name"
                                    rel="noopener noreferrer"
                                >{{ member.fullName }}</a>
                                <a
                                    v-if="member.phone"
                                    class="phone"
                                    :href="'tel:' + member.phone"
                                    target="_top"
                                    rel="noopener noreferrer"
                                >
                                    <span
                                        class="iconfont icon-call-small"
                                    ></span>
                                    <span>
                                        {{ $ft.formatPhone(member.phone) }}
                                    </span>
                                </a>
                                <a
                                    v-if="member.email"
                                    class="email"
                                    :href="'mailto:' + member.email"
                                    rel="noopener noreferrer"
                                >
                                    <span
                                        class="iconfont icon-email-small"
                                    ></span>
                                    <span>{{ member.email }}</span>
                                </a>
                                <a
                                    v-if="member.externalUrl"
                                    class="link"
                                    :href="$ft.url(member.externalUrl)"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <span class="iconfont icon-link"></span>
                                    <span>{{ member.externalUrl }}</span>
                                </a>
                                <div
                                    v-if="
                                        member.agentSocialNetworks &&
                                            member.agentSocialNetworks.length > 0
                                    "
                                    class="agent-social"
                                >
                                    <a
                                        v-for="(link, i) in member.agentSocialNetworks"
                                        v-show="link.socialNetwork && link.url"
                                        :key="i"
                                        :class="[
                                            'iconfont',
                                            'icon-' +
                                                (iconsmap[link.socialNetwork] ||
                                                    link.socialNetwork)
                                        ]"
                                        :href="link.url"
                                        :aria-label="link.socialNetwork"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    ></a>
                                </div>
                            </div>
                            <div class="mobile-agent-social">
                                <div
                                    v-if="
                                        member.agentSocialNetworks &&
                                            member.agentSocialNetworks.length > 0
                                    "
                                    class="agent-social"
                                >
                                    <a
                                        v-for="(link, i) in member.agentSocialNetworks"
                                        v-show="link.socialNetwork && link.url"
                                        :key="i"
                                        :class="[
                                            'iconfont',
                                            'icon-' +
                                                (iconsmap[link.socialNetwork] ||
                                                    link.socialNetwork)
                                        ]"
                                        :href="link.url"
                                        target="_blank"
                                        :aria-label="link.socialNetwork"
                                        rel="noopener noreferrer"
                                    ></a>
                                </div>
                            </div>
                        </Slide>
                    </Swiper>
                </div>
                <div class="page"></div>
            </section>
            <div class="get-more-simple">
                <h2 class="title">
                    {{ messageOption.title
                    }}{{ messageOption.required ? '*' : '' }}
                </h2>
                <v-form
                    class="info-form"
                    :onSuccess="registerSubmitSuccess"
                    ref="form"
                >
                    <v-input
                        :required="messageOption.required"
                        class="message"
                        tag="textarea"
                        name="question"
                        :placeholder="messageOption.placeholder"
                    />
                    <p class="fill-tips">
                        {{ $t('md-form.pleaseFillOutThis') }}
                    </p>
                    <button
                        class="submit"
                        v-html="submitOption || $t('md-form.submit')"
                        :disabled="submitDisabled"
                        :class="{ 'submit-disabled': submitDisabled }"
                        :style="{
                            backgroundColor: submitBgColor,
                        }"
                    ></button>
                </v-form>
            </div>
            <form-toast
                v-model:visible="formToastVisible"
                v-bind="formToastOption"
                @confirm="handleToastConfirm"
            />
        </module-box>
    </div>
</template>

<script>
import { mapState as rootMapState } from 'pinia';
import { mapState } from '@/store/helpers';
import { useUserStore, usePageStore } from '@/store';
import Common from '../index';
import signlog from '../../../components/sign-log';

import VForm from '../../../components/v-form';
import VInput from '../../../components/v-input';
import formToast from '@/components/form-toast';
import { normal } from '../../defaultSpacing';
import { Swiper, SwiperSlide, fixSwiperOptions } from '@/common/swiper';

export default {
    mixins: [Common],
    components: {
        Swiper,
        Slide: SwiperSlide,
        VForm,
        VInput,
        formToast
    },
    computed: {
        ...rootMapState(usePageStore, {
            'page': store => store.page
        }),
        ...rootMapState(useUserStore, {
            'user': store => store.user
        }),
        ...mapState({
            'teamMemberAgents': store => store.agents
        }),
        options() {
            return fixSwiperOptions({
                speed: 1000,
                spaceBetween: 20,
                pagination: {
                    closest: '.md-team-member',
                    el: '.page',
                    clickable: true
                },
                autoplay: true
            });
        },
        messageOption() {
            let options = {};
            this.formList.forEach(item => {
                if (item.name === 'question') {
                    options = {
                        title: item.title,
                        placeholder:
                            item.placeholder ||
                            this.$t('md-form.whatWouldYouLikeTo'),
                        required: item.required
                    };
                }
            });
            return options
        }
    },
    data: function() {
        return {
            agentIds: '',
            iconsmap: {
                facebook: 'facebook-sign',
                'google+': 'google2',
                redfin: 'redfin-simple',
                twitter: 'newnewtwitter'
            },
            submitDisabled: false,
            ...normal
        };
    },
    methods: {
        registerSubmitSuccess(data = {}) {
            this.submitDisabled = true;
            setTimeout(() => {
                this.submitDisabled = false;
            }, 3000);
            this.data = data;
            this.tag && (data.tagIds = this.tag); //SITE-12720
            if (!this.user) {
                signlog.register({
                    role: this.registerRole,
                    languageTextFn: {
                        registerTitle: _ => this.$t('md-form.leaveAMessage'),
                        registerSubTitle: _ =>
                            this.$t('md-form.haveAllOfYourQuestions')
                    },
                    registerData: data,
                    onsuccess: () => {
                        this.leaveMessage(data);
                        return false;
                    }
                });
            } else {
                this.leaveMessage(data);
            }
        },
        leaveMessage(data) {
            data.otherMsg = this.page.page || '';
            data.name = this.user.fullName || '';
            data.phoneNum = this.user.phoneNumber || '';
            data.emailAddress = this.user.account || '';
            data.pageUrl = this.pageLocation.href;
            // listingId and isSoldListing  rear end GlobalData Give
            if (this.page.listingId !== undefined) {
                data.listingId = this.page.listingId;
            }
            if (this.page.isSoldListing !== undefined) {
                data.sold = this.page.isSoldListing;
            }
            this.axiosIns
                .post('/api-site/leaveMsg', data)
                .then(this.onSuccessCallback)
                .catch(e => {
                    console.log(e);
                });

            this.$EventBus.$emit('siteTrack', {
                msg: data.question || '',
                trackType: 'leave-message'
            });
        },
        onSuccessCallback() {
            this.handleShowToast();
            if (this.$refs.form) {
                this.$refs.form.reset({
                    question: ''
                });
            }
        }
    }
};
</script>
<style lang="scss">
@import '../../../theme/mixin.scss';
@import '../../../theme/media.scss';
.md-form.team-slider {
    .team-slider-content {
        display: flex;
        .md-team-member {
            margin-right: 40px;
            width: 520px;
            max-width: calc(100% - 300px);
            @media (max-width: 900px) {
                margin-right: 20px;
            }
            @media (max-width: 800px) {
                // display: none;
                margin: 0;
                width: 100%;
                max-width: 100%;
            }
            .icon-facebook-sign {
                background-color: #4b67a0;
            }
            .icon-linkedin {
                background-color: #007ab9;
            }
            .icon-newnewtwitter {
                background-color: #000;
            }
            .icon-zillow {
                background-color: #0071e8;
            }
            .icon-yelp {
                background-color: #d84132;
            }
            .icon-youtube {
                background-color: #db3533;
            }

            .icon-snapchat {
                background: url('//static.chimeroi.com/site/images/about/snapchat-44.png')
                    0 1px no-repeat;
                background-position: 50%;
                background-size: 22px 22px;
                background-color: #fae500;
            }

            .icon-instagram {
                background-image: url('//static.chimeroi.com/site/images/logo/instagram.png');
                background-size: cover;
            }
            .icon-pinterest {
                background-color: #cd2324;
            }
            .icon-angelist {
                background-color: #000;
            }
            .icon-google2 {
                background-color: #d13e2f;
            }

            .icon-whatsapp {
                background-color: #00d41b;
            }
            .icon-redfin-simple {
                background-color: #c11011;
            }
            .title {
                margin-bottom: 20px;
                font-family: var(--mg-title-font);
                font-size: 24px;
                text-align: left;
                line-height: 150%;
            }
            .slides {
                height: 202px;
                position: relative;
                overflow: hidden;
                .member-slider {
                    position: absolute;
                    top: 0px;
                    left: 0px;
                    right: 0px;
                    bottom: 0px;
                    .swiper-slide {
                        height: 100%;
                        width: 100%;
                        border: 1px solid #dcdcdc;
                        background: white;
                        box-sizing: border-box;
                        padding: 15px;
                        display: flex;
                        flex-wrap: wrap;
                        .agent-image {
                            height: 170px;
                            width: 170px;
                            position: relative;
                            overflow: hidden;
                            float: left;
                            @media (max-width: 800px) {
                                width: 100px;
                                height: 100px;
                            }
                        }
                        .content {
                            display: flex;
                            flex-direction: column;
                            align-items: flex-start;
                            height: 100%;
                            margin-left: 15px;
                            width: calc(100% - 185px);
                            @media (max-width: 800px) {
                                width: calc(100% - 115px);
                                height: 100px;
                            }
                            .name {
                                font-size: 20px;
                                line-height: 24px;
                                text-overflow: ellipsis;
                                max-width: 100%;
                                overflow: hidden;
                                white-space: nowrap;
                                flex: none;
                                width: 100%;
                                height: 24px;
                                font-family: var(--font-bold);
                            }
                            a.name {
                                &:hover {
                                    @include color-theme(color);
                                }
                            }

                            .phone {
                                margin-top: 11px;
                                font-size: 14px;
                                line-height: 22px;
                                height: 22px;
                                flex: none;
                                width: 100%;
                                text-overflow: ellipsis;
                                max-width: 100%;
                                overflow: hidden;
                                white-space: nowrap;
                                &:hover {
                                    @include color-theme(color);
                                }
                                .iconfont {
                                    margin-right: 2px;
                                }
                            }
                            .email {
                                font-size: 14px;
                                line-height: 22px;
                                height: 22px;
                                flex: none;
                                width: 100%;
                                text-overflow: ellipsis;
                                max-width: 100%;
                                overflow: hidden;
                                white-space: nowrap;
                                &:hover {
                                    @include color-theme(color);
                                }
                                .iconfont {
                                    margin-right: 3px;
                                }
                            }
                            .iconfont{
                                color: #a8a8a8;
                            }
                            .link {
                                font-size: 14px;
                                line-height: 22px;
                                height: 22px;
                                flex: none;
                                width: 100%;
                                text-overflow: ellipsis;
                                max-width: 100%;
                                overflow: hidden;
                                white-space: nowrap;
                                &:hover {
                                    @include color-theme(color);
                                }
                                .iconfont {
                                    vertical-align: middle;
                                }
                            }
                            .agent-social {
                                display: flex;
                                flex-wrap: wrap;
                                @media (max-width: 800px) {
                                    display: none;
                                }
                                .iconfont {
                                    display: inline-block;
                                    margin-right: 10px;
                                    margin-top: 10px;
                                    width: 25px;
                                    height: 25px;
                                    line-height: 25px;
                                    text-align: center;
                                    border-radius: 50%;
                                    color: #fff;
                                    @media (max-width: 900px) {
                                        margin-right: 2px;
                                    }
                                    &:before {
                                        color: #fff;
                                        margin: 0;
                                    }
                                }
                            }
                        }
                        .mobile-agent-social {
                            width: 100%;
                            display: none;
                            @media (max-width: 800px) {
                                display: flex;
                            }
                            .agent-social {
                                display: flex;
                                flex-wrap: wrap;
                                .iconfont {
                                    display: inline-block;
                                    margin-right: 8px;
                                    margin-top: 8px;
                                    width: 25px;
                                    height: 25px;
                                    line-height: 25px;
                                    text-align: center;
                                    border-radius: 50%;
                                    color: #fff;
                                    &:before {
                                        color: #fff;
                                        margin: 0;
                                    }
                                }
                            }
                        }
                    }
                }
            }
            .page {
                display: flex;
                height: 30px;
                justify-content: center;
                align-items: center;
                .swiper-pagination-bullet {
                    height: 6px;
                    width: 30px;
                    border-radius: 3px;
                    @include color-theme('background-color');
                    opacity: 0.2;
                    &:focus {
                        outline: none;
                    }
                }
                .swiper-pagination-bullet-active {
                    opacity: 1;
                }
            }
        }

        .get-more-simple {
            width: calc(100% - 560px);
            flex: 1;
            margin-top: 0px;
            @media (max-width: 800px) {
                width: 100%;
            }
            &:after {
                content: '';
                display: block;
                clear: both;
            }
            .title {
                color: #303030;
                margin-bottom: 20px;
                font-family: var(--font-bold);
                font-size: 24px;
                text-align: left;
                text-transform: uppercase;
                @media (max-width: 800px) {
                    font-size: 20px;
                }
            }
            .info-form {
                textarea {
                    color: #505050;
                    width: 100%;
                    height: 200px;
                    padding: 14px;
                    border-radius: 2px;
                    resize: none;
                    box-sizing: border-box;
                    border: 1px solid #dcdcdc;
                    box-shadow: inset 0 0 1px 0 #ccc;
                    background: white;
                    margin-top: 0px;
                    font-size: 18px;
                    &:focus {
                        @include color-theme(border-color);
                    }
                    &::-webkit-input-placeholder {
                        font-size: 18px;
                        color: #a8a8a8;
                    }
                    @media (max-width: 800px) {
                        font-size: 14px;
                    }
                }
                .fill-tips {
                    color: rgb(232, 64, 55);
                    display: none;
                    font-size: 16px;
                }
                .submit {
                    @include color-button-hover();
                    display: block;
                    cursor: pointer;
                    border: none;
                    float: right;
                    min-width: 120px;
                    height: 40px;
                    margin-top: 30px;
                    border-radius: 2px;
                    font-size: 18px;
                    text-transform: uppercase;
                    @media (max-width: 800px) {
                        float: left;
                    }
                    @include color-button();
                }
                input[disabled='disabled'].submit-disabled {
                    @include input-disabled();
                }
            }
        }
    }
    @include media-600 {
        .team-slider-content {
            flex-direction: column;
        }
    }
}
</style>
